import { getAddress } from "ethers/address";
import { createStore, EIP6963ProviderDetail, Store } from "mipd";
import { chainIdToPlatform } from "./platform";
import {
  ExternalPlatform,
  ExternalWallet,
  ExternalWalletResponse,
  ExternalWalletType,
  WalletAdapter,
} from "./types";

// Shared store across all EthereumWalletBase instances so late EIP-6963
// announcements are captured once and visible to every wallet adapter.
let sharedStore: Store | undefined;

function getSharedStore(): Store {
  if (!sharedStore) {
    sharedStore = createStore();
  }
  return sharedStore;
}

export abstract class EthereumWalletBase implements WalletAdapter {
  abstract readonly type: ExternalWalletType;
  abstract readonly rdns: string;
  abstract readonly displayName: string;

  platform: ExternalPlatform | undefined;
  protected account: string | undefined = undefined;
  protected provider: EIP6963ProviderDetail | undefined;
  protected connectedAccounts: string[] = [];

  constructor() {
    this.initializeIfAvailable();
  }

  private getProvider(): EIP6963ProviderDetail | undefined {
    // Use shared store's findProvider which reflects late announcements
    const found = getSharedStore().findProvider({ rdns: this.rdns as any });
    if (found) {
      this.provider = found;
    }
    return this.provider;
  }

  private getEthereumProvider(): any {
    const provider = this.getProvider();
    if (provider) {
      return provider.provider;
    }

    return this.getFallbackProvider();
  }

  /**
   * Fallback provider detection when EIP-6963 announcement is missed.
   * Subclasses can override to provide wallet-specific fallback logic.
   */
  protected getFallbackProvider(): any {
    return null;
  }

  private initializeIfAvailable(): void {
    const provider = this.getProvider();
    if (provider && !this.initialized) {
      this.initialized = true;
      this.initializeProvider();
    }
  }

  private initialized = false;

  private initializeProvider(): void {
    const provider = this.getProvider();
    if (!provider) return;

    provider.provider
      .request({
        method: "eth_accounts",
      })
      .then((accounts) => {
        this.connectedAccounts = accounts.map(getAddress);
        if (accounts.length > 0) {
          this.account = getAddress(accounts[0]);
        }
      })
      .catch(console.error);

    provider.provider
      .request({
        method: "eth_chainId",
      })
      .then((chainId) => {
        this.platform = chainIdToPlatform(chainId);
      })
      .catch(console.error);

    provider.provider?.on("chainChanged", (chainId: string) => {
      this.platform = chainIdToPlatform(chainId);
    });

    provider.provider?.on("accountsChanged", (accounts: string[]) => {
      if (accounts) {
        this.connectedAccounts = accounts.map((account) => getAddress(account));
        this.account =
          accounts.length > 0 ? getAddress(accounts[0]) : undefined;
      }
    });
  }

  isAvailable(): boolean {
    // Check dynamically each time, as the provider might be announced after instantiation
    const provider = this.getProvider();

    // Initialize if we just found the provider
    if (provider && !this.initialized) {
      this.initializeIfAvailable();
    }

    if (provider) {
      return true;
    }

    // Fall back to wallet-specific detection when EIP-6963 announcement is missed
    return typeof window !== "undefined" && !!this.getFallbackProvider();
  }

  getInfo(): ExternalWallet {
    const available = this.isAvailable();

    return {
      type: this.type,
      available,
      version: available ? window.ethereum?.version || "Unknown" : undefined,
      chainId: available ? window.ethereum?.chainId : undefined,
      name: this.displayName,
      platform: this.platform,
      connectedAccounts: this.connectedAccounts,
    };
  }

  getConnectedAccounts(): string[] {
    return this.connectedAccounts;
  }

  async connect(address?: string): Promise<ExternalWalletResponse<any>> {
    if (address && this.connectedAccounts.includes(getAddress(address))) {
      this.account = getAddress(address);
    }

    if (this.account) {
      return { success: true, wallet: this.type, account: this.account };
    }

    try {
      if (!this.isAvailable()) {
        throw new Error(`${this.displayName} is not available`);
      }

      const ethereum = this.getEthereumProvider();

      if (!ethereum) {
        throw new Error(`${this.displayName} provider not found`);
      }

      const accounts = await ethereum.request({
        method: "eth_requestAccounts",
      });

      if (accounts && accounts.length > 0) {
        this.account = getAddress(accounts[0]);
        this.connectedAccounts = accounts.map(getAddress);

        // If we used a fallback provider, store it for future use
        if (!this.getProvider()) {
          this.provider = {
            info: {
              uuid: `${this.rdns}-fallback`,
              name: this.displayName,
              icon: "data:image/svg+xml;base64,",
              rdns: this.rdns,
            },
            provider: ethereum,
          } as EIP6963ProviderDetail;
          this.initializeIfAvailable();
        }

        return { success: true, wallet: this.type, account: this.account };
      }

      throw new Error("No accounts found");
    } catch (error) {
      console.error(`Error connecting to ${this.displayName}:`, error);
      return {
        success: false,
        wallet: this.type,
        error: (error as Error).message || "Unknown error",
      };
    }
  }

  async signTransaction(
    transaction: any,
  ): Promise<ExternalWalletResponse<any>> {
    try {
      if (!this.isAvailable() || !this.account) {
        throw new Error(`${this.displayName} is not connected`);
      }

      const ethereum = this.getEthereumProvider();
      if (!ethereum) {
        throw new Error(`${this.displayName} is not connected`);
      }

      const result = await ethereum.request({
        method: "eth_sendTransaction",
        params: [transaction],
      });

      return { success: true, wallet: this.type, result };
    } catch (error) {
      console.error(
        `Error signing transaction with ${this.displayName}:`,
        error,
      );
      return {
        success: false,
        wallet: this.type,
        error: (error as Error).message || "Unknown error",
      };
    }
  }

  async signMessage(
    message: string | `0x${string}`,
    address?: string,
  ): Promise<ExternalWalletResponse<any>> {
    try {
      if (!this.isAvailable() || !this.account) {
        throw new Error(`${this.displayName} is not connected`);
      }

      const ethereum = this.getEthereumProvider();
      if (!ethereum) {
        throw new Error(`${this.displayName} provider not found`);
      }
      const result = await ethereum.request({
        method: "personal_sign",
        params: [message, address || this.account] as any,
      });

      return { success: true, wallet: this.type, result };
    } catch (error) {
      console.error(`Error signing message with ${this.displayName}:`, error);
      return {
        success: false,
        wallet: this.type,
        error: (error as Error).message || "Unknown error",
      };
    }
  }

  async signTypedData(data: any): Promise<ExternalWalletResponse<any>> {
    try {
      if (!this.isAvailable() || !this.account) {
        throw new Error(`${this.displayName} is not connected`);
      }

      const ethereum = this.getEthereumProvider();
      if (!ethereum) {
        throw new Error(`${this.displayName} is not connected`);
      }

      const result = await ethereum.request({
        method: "eth_signTypedData_v4",
        params: [this.account, JSON.stringify(data)] as any,
      });

      return { success: true, wallet: this.type, result };
    } catch (error) {
      console.error(
        `Error signing typed data with ${this.displayName}:`,
        error,
      );
      return {
        success: false,
        wallet: this.type,
        error: (error as Error).message || "Unknown error",
      };
    }
  }

  async sendTransaction(txn: any): Promise<ExternalWalletResponse<any>> {
    try {
      if (!this.isAvailable() || !this.account) {
        throw new Error(`${this.displayName} is not connected`);
      }

      const ethereum = this.getEthereumProvider();
      if (!ethereum) {
        throw new Error(`${this.displayName} is not connected`);
      }

      const result = await ethereum.request({
        method: "eth_sendTransaction",
        params: [txn],
      });

      return { success: true, wallet: this.type, result };
    } catch (error) {
      console.error(
        `Error sending transaction with ${this.displayName}:`,
        error,
      );
      return {
        success: false,
        wallet: this.type,
        error: (error as Error).message || "Unknown error",
      };
    }
  }

  async switchChain(chainId: string): Promise<boolean> {
    try {
      if (!this.isAvailable()) {
        throw new Error(`${this.displayName} is not available`);
      }

      const ethereum = this.getEthereumProvider();
      if (!ethereum) {
        throw new Error(`${this.displayName} is not connected`);
      }

      try {
        await ethereum.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId }],
        });

        this.platform = chainIdToPlatform(chainId);
        return true;
      } catch (error) {
        if ((error as any).code === 4902) {
          console.warn(`Chain not added to ${this.displayName}`);
        }
        throw error;
      }
    } catch (error) {
      console.error(`Error switching chain for ${this.displayName}:`, error);
      return false;
    }
  }

  async getBalance(
    tokenAddress?: string,
  ): Promise<ExternalWalletResponse<any>> {
    try {
      if (!this.isAvailable() || !this.account) {
        throw new Error(`${this.displayName} is not connected`);
      }

      if (tokenAddress) {
        return {
          success: false,
          wallet: this.type,
          error: "Not implemented for ERC20",
        };
      } else {
        const ethereum = this.getEthereumProvider();
        if (!ethereum) {
          throw new Error(`${this.displayName} is not connected`);
        }

        const balance = await ethereum.request({
          method: "eth_getBalance",
          params: [this.account, "latest"] as any,
        });
        return { success: true, wallet: this.type, result: balance };
      }
    } catch (error) {
      console.error(`Error getting balance from ${this.displayName}:`, error);
      return {
        success: false,
        wallet: this.type,
        error: (error as Error).message || "Unknown error",
      };
    }
  }

  async waitForTransaction(
    txHash: string,
    timeoutMs: number = 60000,
  ): Promise<ExternalWalletResponse<any>> {
    try {
      if (!this.isAvailable()) {
        throw new Error(`${this.displayName} is not connected`);
      }

      const ethereum = this.getEthereumProvider();
      if (!ethereum) {
        throw new Error(`${this.displayName} is not connected`);
      }

      const startTime = Date.now();
      const pollInterval = 1000; // 1 second

      while (Date.now() - startTime < timeoutMs) {
        const receipt = await ethereum.request({
          method: "eth_getTransactionReceipt",
          params: [txHash as `0x${string}`],
        });

        if (receipt) {
          return {
            success: true,
            wallet: this.type,
            result: receipt,
          };
        }

        // Wait before polling again
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }

      throw new Error("Transaction confirmation timed out");
    } catch (error) {
      console.error(
        `Error waiting for transaction with ${this.displayName}:`,
        error,
      );
      return {
        success: false,
        wallet: this.type,
        error: (error as Error).message || "Unknown error",
      };
    }
  }
}
